import {Component, OnInit, TemplateRef} from '@angular/core';
import {PropertiesService} from '../../../services/api/properties-service.service';
import {ModalService} from '../../../services/modal/modal.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {CatalogModel, PropertyModel} from '../../../models/property.model';
import {AlertService} from '../../../services/notify/alert.service';
import {HelpersService} from '../../../services/helpers.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-properties',
    templateUrl: './properties.component.html',
    styleUrls: ['./properties.component.scss']
})
export class PropertiesComponent implements OnInit {
    tab: 'board' | 'doc' | 'contract' = 'board';
    propertyTarget = '';

    keys: [
        'property_file',
        'property_member',
        'property_text',
        'property_boolean',
        'property_catalog',
        'property_todo',
    ] = [
        'property_file',
        'property_member',
        'property_text',
        'property_boolean',
        'property_catalog',
        'property_todo',
    ];
    propertyTextTemplate: { [key: string]: { head: string, empty: string, icon: string } };

    properties: {
        property_file: PropertyModel[],
        property_member: PropertyModel[],
        property_text: PropertyModel[],
        property_boolean: PropertyModel[],
        property_catalog: PropertyModel[],
        property_todo: PropertyModel[],
    } = {
        property_file: [],
        property_member: [],
        property_text: [],
        property_boolean: [],
        property_catalog: [],
        property_todo: [],
    };

    selectedProperty: PropertyModel;

    allCatalogs: CatalogModel[] = [];

    isMultiform = false;

    propertyInfo: any;

    loading = false;

    constructor(
        public translate: TranslateService,
        private propertiesService: PropertiesService,
        public modalService: ModalService,
        private alertService: AlertService,
        public helper: HelpersService,
    ) {
    }

    ngOnInit(): void {
        this.getProperties(true, this.tab);

        this.propertiesService.getAllCatalogs().subscribe({
            next: res => {
                this.allCatalogs = res.catalog;
            }
        });
    }

    getProperties(reload: boolean, tab: 'board' | 'doc' | 'contract'): void {
        this.loading = true;
        this.tab = tab;

        if (reload) {
            this.propertiesService.getProperties(this.tab).subscribe({
                next: res => {
                    this.propertyTextTemplate = {
                        property_file: {
                            head: this.translate.instant('PROPERTIES.GROUPS.DOCUMENTS'),
                            empty: this.translate.instant('EMPTY_LISTS.PROPERTIES.DOCUMENTS'),
                            icon: 'bi bi-file-earmark-word mr-1',
                        },
                        property_member: {
                            head: this.translate.instant('PROPERTIES.GROUPS.MEMBER'),
                            empty: this.translate.instant('EMPTY_LISTS.PROPERTIES.MEMBER'),
                            icon: 'bi bi-person mr-1',
                        },
                        property_text: {
                            head: this.translate.instant('PROPERTIES.GROUPS.TEXT'),
                            empty: this.translate.instant('EMPTY_LISTS.PROPERTIES.TEXT'),
                            icon: 'bbi bi-card-text mr-1',
                        },
                        property_boolean: {
                            head: this.translate.instant('PROPERTIES.GROUPS.BOOLEAN'),
                            empty: this.translate.instant('EMPTY_LISTS.PROPERTIES.BOOLEAN'),
                            icon: 'bi bi-check2-circle mr-1',
                        },
                        property_catalog: {
                            head: this.translate.instant('PROPERTIES.GROUPS.CATALOG'),
                            empty: this.translate.instant('EMPTY_LISTS.PROPERTIES.CATALOG'),
                            icon: 'bi bi-justify mr-1',
                        },
                        property_todo: {
                            head: this.translate.instant('PROPERTIES.GROUPS.TODO'),
                            empty: this.translate.instant('EMPTY_LISTS.PROPERTIES.TODO'),
                            icon: 'bi bi-clipboard2-check mr-1',
                        },
                    };
                    this.properties = {
                        property_file: [],
                        property_member: [],
                        property_text: [],
                        property_boolean: [],
                        property_catalog: [],
                        property_todo: [],
                    };

                    res.properties.map((item: PropertyModel) => {
                        this.properties[item.property_type].push(item);
                    });
                    this.loading = false;
                }, error: err => {
                    this.alertService.setAlert(err.message, 'blue');
                }
            });
        }
    }

    createProperty(propName: string, propType: string, modal: NgbModalRef): void {
        const data = {
            type: this.tab,
            title: propName,
            property_type: propType,
            property_type_target: this.propertyTarget,
            is_multiform: this.isMultiform,
        };

        this.propertiesService.createProperty(data).subscribe({
            next: res => {
                this.getProperties(true, this.tab);
                this.propertyTarget = '';
                this.alertService.setAlert(res.message, 'green');
                modal.close();
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    openProperty(prop: PropertyModel, content: TemplateRef<any>, size: 'sm' | 'lg'): void {
        this.selectedProperty = structuredClone(prop);
        this.modalService.openTemplateModal(content, size);

        if (size === 'sm' && ['board', 'doc'].includes(this.tab)) {
            this.propertiesService.getPropertyInfoById(this.tab as 'board' | 'doc', prop.id).subscribe({
                next: res => {
                    this.propertyInfo = res.relations;
                }, error: err => {
                    this.alertService.setAlert(err.message, 'blue');
                }
            });
        }
    }

    updateProperty(modal: NgbModalRef): void {
        const data = {
            type: this.tab,
            id: this.selectedProperty.id,
            title: this.selectedProperty.title,
            property_type_target: this.selectedProperty.property_type_target,
        };
        this.propertiesService.updateProperty(data).subscribe({
            next: () => {
                this.getProperties(true, this.tab);
                modal.close();
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    deleteProperty(prop: PropertyModel): void {
        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_PROPERTY', {title: prop.title}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.propertiesService.deleteProperty(prop.id, this.tab).subscribe({
                    next: res => {
                        this.alertService.setAlert(res.message, 'green');
                        this.getProperties(true, this.tab);
                    }, error: err => {
                        this.alertService.setAlert(err.message, 'blue');
                    }
                });
            }
        }).catch(() => {
            return;
        });
    }
}
